import { ChangeEvent, FC, useState } from "react";
import { createPortal } from "react-dom";
import { MapCustomControlProps } from "@app/shared/mapping/MapCustomControlProps";
import { useMapboxControl } from "@app/shared/mapping/useMapBoxControl";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { ZIP_POINTS_INFO } from "@app/shared/mapping/zipPoints";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import TooltipWrapper from "@app/shared/TooltipWrapper";

interface ZipCoverageMapLayerControlProps extends MapCustomControlProps {
  lightStyle: string;
  outdoorsStyle: string;
  mapStyle: string;
  onMapStyleChange: (style: string) => void;
  showMembersWithAccess: boolean;
  onShowMembersWithAccessChange: (checked: boolean) => void;
  showMembersWithoutAccess: boolean;
  onShowMembersWithoutAccessChange: (checked: boolean) => void;
  showZipBoundary: boolean;
  onShowZipBoundaryChange: (checked: boolean) => void;
  showZipPoints: boolean;
  onShowZipPointsChange: (checked: boolean) => void;
  showCounty: boolean;
  onShowCountyChange: (checked: boolean) => void;
}

export const ZipCoverageMapLayerControl: FC<ZipCoverageMapLayerControlProps> = ({
  ignoreInMapExport,
  imagePath,
  mapRef,
  position,
  lightStyle,
  outdoorsStyle,
  mapStyle,
  onMapStyleChange,
  showMembersWithAccess,
  onShowMembersWithAccessChange,
  showMembersWithoutAccess,
  onShowMembersWithoutAccessChange,
  showZipBoundary,
  onShowZipBoundaryChange,
  showZipPoints,
  onShowZipPointsChange,
  showCounty,
  onShowCountyChange,
}) => {
  const [showLayerControl, setShowLayerControl] = useState(false);

  const handleMembersWithAccessChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onShowMembersWithAccessChange(e.target.checked);
  };

  const handleMembersWithoutAccessChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onShowMembersWithoutAccessChange(e.target.checked);
  };

  const handleZipBoundaryChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onShowZipBoundaryChange(e.target.checked);
  };

  const handleZipPointsChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onShowZipPointsChange(e.target.checked);
  };

  const handleCountyChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onShowCountyChange(e.target.checked);
  };

  const container = useMapboxControl({ mapRef, position });
  if (!container) return null;

  return createPortal(
    <div
      className="layer-control-main-container"
      {...(ignoreInMapExport ? { "data-html2canvas-ignore": "" } : {})}
    >
      <div
        className="layer-hover-container"
        data-testid="zip-coverage-map-layer-control-toggle"
        onClick={() => setShowLayerControl((prev) => !prev)}
      >
        <img src={imagePath} className="layer-hover-icon" />
      </div>

      {showLayerControl && (
        <div className="layer-control-container">
          <span className="provider-member-layer-control__title">Members</span>
          <div className="provider-member-layer-control__layers-container">
            <label className="provider-member-layer-control__label">
              <QESCheckbox
                checked={showMembersWithAccess}
                onChange={handleMembersWithAccessChange}
                label="With Access"
                name="withAccess"
                testId="zip-coverage-map-members-with-access-checkbox"
              />
            </label>
            <label className="provider-member-layer-control__label">
              <QESCheckbox
                checked={showMembersWithoutAccess}
                onChange={handleMembersWithoutAccessChange}
                label="Without Access"
                name="withoutAccess"
                testId="zip-coverage-map-members-without-access-checkbox"
              />
            </label>
          </div>

          <span className="provider-member-layer-control__title">Map Style</span>
          <div className="provider-member-layer-control__layers-container">
            <label className="provider-member-layer-control__label">
              <QESRadioButton
                name="zip-coverage-map-style"
                checked={mapStyle === lightStyle}
                onChange={(e) => onMapStyleChange(e.target.value)}
                label="Light"
                testId="zip-coverage-map-style-light-radio"
                value={lightStyle}
              />
            </label>
            <label className="provider-member-layer-control__label">
              <QESRadioButton
                name="zip-coverage-map-style"
                checked={mapStyle === outdoorsStyle}
                onChange={(e) => onMapStyleChange(e.target.value)}
                label="Terrain"
                testId="zip-coverage-map-style-terrain-radio"
                value={outdoorsStyle}
              />
            </label>
          </div>

          <span className="provider-member-layer-control__title">Boundaries</span>
          <div className="provider-member-layer-control__layers-container">
            <label className="provider-member-layer-control__label">
              <QESCheckbox
                checked={showZipBoundary}
                onChange={handleZipBoundaryChange}
                label="Zip Code"
                name="zipCode"
                testId="zip-coverage-map-layer-zipcode-checkbox"
              />
            </label>
            <div className="provider-member-layer-control__nested-row">
              <label className="provider-member-layer-control__label">
                <QESCheckbox
                  checked={showZipPoints}
                  disabled={!showZipBoundary}
                  onChange={handleZipPointsChange}
                  label="Include Zip Points"
                  name="zipPoints"
                  testId="zip-coverage-map-layer-zip-points-checkbox"
                />
              </label>
              <TooltipWrapper text={ZIP_POINTS_INFO} position="left" tooltipWidth="20rem">
                <QesIcon iconName={QesIcons.InfoCircle} color="#828282" height="16px" width="16px" description="About Zip Points" />
              </TooltipWrapper>
            </div>
            <label className="provider-member-layer-control__label">
              <QESCheckbox
                checked={showCounty}
                onChange={handleCountyChange}
                label="County"
                name="county"
                testId="zip-coverage-map-layer-county-checkbox"
              />
            </label>
          </div>
        </div>
      )}
    </div>,
    container,
  );
};

export default ZipCoverageMapLayerControl;
